/**
 * Boş səhifə nəticələrini əvvəl UI-da göstərir (şəkillər yüklənir),
 * sonra "Indir" basıldıqda ZIP-i DOM-dakı şəkillərdən yaradır (CORS-siz).
 */

import React, { useState, useRef, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import {
  EmptyPageExportItem,
  downloadEmptyPageResultsAsZipFromImages,
  FOLDER_CONFIRMED,
  FOLDER_REJECTED,
} from '../../utils/emptyPageZipExport';

export interface EmptyPageZipModalItem {
  studentId: string;
  studentName: string;
  questionNumber: string;
  questionData: { path?: string };
  verificationStatus?: 'pending' | 'confirmed' | 'rejected';
}

const ModalOverlay = styled.div<{ $show: boolean }>`
  display: ${(p) => (p.$show ? 'flex' : 'none')};
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.75);
  z-index: 2100;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const ModalBox = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  max-width: 960px;
  width: 100%;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-shrink: 0;
`;

const ModalTitle = styled.h2`
  font-size: 22px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  font-size: 28px;
  cursor: pointer;
  color: #64748b;
  padding: 4px 8px;
  line-height: 1;
  &:hover {
    color: #1e293b;
  }
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #334155;
  margin: 0 0 12px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 12px;
  margin-bottom: 24px;
  max-height: 45vh;
  overflow-y: auto;
  padding: 12px;
  background: #f8fafc;
  border-radius: 12px;
`;

const Card = styled.div`
  background: white;
  border-radius: 10px;
  overflow: hidden;
  border: 2px solid #e2e8f0;
  transition: border-color 0.2s;
  &:hover {
    border-color: #3b82f6;
  }
`;

const CardInfo = styled.div`
  padding: 8px 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  color: #64748b;
  font-weight: 600;
`;

const CardImg = styled.img`
  width: 100%;
  height: 180px;
  object-fit: contain;
  background: #f1f5f9;
  display: block;
`;

const LoadStatus = styled.div`
  padding: 12px 0;
  font-size: 14px;
  color: #64748b;
  text-align: center;
  flex-shrink: 0;
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding-top: 16px;
  border-top: 2px solid #e2e8f0;
  flex-shrink: 0;
`;

const Btn = styled.button<{ $primary?: boolean }>`
  padding: 12px 24px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  background: ${(p) =>
    p.$primary
      ? 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)'
      : 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)'};
  color: ${(p) => (p.$primary ? 'white' : '#475569')};
  &:hover:not(:disabled) {
    opacity: 0.9;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ErrorText = styled.div`
  font-size: 13px;
  color: #dc2626;
  margin-top: 8px;
`;

function itemKey(item: EmptyPageExportItem): string {
  return `${item.studentId}-${item.questionNumber}`;
}

export interface EmptyPageZipModalProps {
  show: boolean;
  onClose: () => void;
  /** Boş səhifə siyahısı (confirmed + rejected olanlar modalda göstərilir və ZIP-ə daxil edilir) */
  emptyPages: EmptyPageZipModalItem[];
  onSuccess?: (total: number, confirmed: number, rejected: number) => void;
  onError?: (message: string) => void;
}

export const EmptyPageZipModal: React.FC<EmptyPageZipModalProps> = ({
  show,
  onClose,
  emptyPages,
  onSuccess,
  onError,
}) => {
  const [loadedSet, setLoadedSet] = useState<Set<string>>(new Set());
  const [errorSet, setErrorSet] = useState<Set<string>>(new Set());
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const imgRefs = useRef<Map<string, HTMLImageElement>>(new Map());

  const toExport = useMemo(() => {
    return emptyPages.filter(
      (p): p is EmptyPageZipModalItem & { verificationStatus: 'confirmed' | 'rejected' } =>
        p.verificationStatus === 'confirmed' || p.verificationStatus === 'rejected'
    ) as EmptyPageExportItem[];
  }, [emptyPages]);

  const confirmedItems = useMemo(() => toExport.filter((p) => p.verificationStatus === 'confirmed'), [toExport]);
  const rejectedItems = useMemo(() => toExport.filter((p) => p.verificationStatus === 'rejected'), [toExport]);

  const totalCount = toExport.length;
  const loadedCount = loadedSet.size + errorSet.size;
  const allSettled = totalCount === 0 || loadedCount >= totalCount;

  const setRef = useCallback((key: string, el: HTMLImageElement | null) => {
    if (!el) return;
    imgRefs.current.set(key, el);
  }, []);

  const handleLoad = useCallback((key: string) => {
    setLoadedSet((prev) => new Set(prev).add(key));
    setErrorSet((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  }, []);

  const handleError = useCallback((key: string) => {
    setErrorSet((prev) => new Set(prev).add(key));
    setLoadedSet((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  }, []);

  const handleDownload = useCallback(async () => {
    if (toExport.length === 0) return;
    setDownloadError(null);
    setDownloading(true);
    try {
      const result = await downloadEmptyPageResultsAsZipFromImages({
        items: toExport,
        getImageElement: (item) => imgRefs.current.get(itemKey(item)) ?? null,
        zipFileName: 'bos_sehife_netice',
      });
      if (result.success) {
        onSuccess?.(result.totalAdded, result.confirmedCount, result.rejectedCount);
        onClose();
      } else {
        const msg = result.errors.slice(0, 3).join('; ') || 'ZIP yaradıla bilmədi';
        setDownloadError(msg);
        onError?.(msg);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setDownloadError(msg);
      onError?.(msg);
    } finally {
      setDownloading(false);
    }
  }, [toExport, onClose, onSuccess, onError]);

  const handleClose = useCallback(() => {
    setLoadedSet(new Set());
    setErrorSet(new Set());
    setDownloadError(null);
    imgRefs.current.clear();
    onClose();
  }, [onClose]);

  if (!show) return null;

  return (
    <ModalOverlay $show={show}>
      <ModalBox>
        <ModalHeader>
          <ModalTitle>Boş kağız nəticəsi – ZIP endir</ModalTitle>
          <CloseBtn type="button" onClick={handleClose} aria-label="Bağla">
            ×
          </CloseBtn>
        </ModalHeader>

        <LoadStatus>
          {totalCount === 0
            ? 'Export üçün təsdiqlənmiş və ya rədd edilmiş səhifə yoxdur.'
            : (
              <>
                Şəkillər yüklənir: {loadedCount} / {totalCount}
                {errorSet.size > 0 && ` (${errorSet.size} yüklənmədi)`}
                {errorSet.size > 0 && (
                  <div style={{ fontSize: '12px', marginTop: '6px', color: '#94a3b8' }}>
                    Bəzi şəkillər yüklənmədi. ZIP endirərkən sunucu vasitəsilə alınacaq.
                  </div>
                )}
              </>
            )}
        </LoadStatus>

        {confirmedItems.length > 0 && (
          <>
            <SectionTitle>{FOLDER_CONFIRMED}</SectionTitle>
            <Grid>
              {confirmedItems.map((item) => {
                const key = itemKey(item);
                const path = item.questionData?.path;
                return (
                  <Card key={key}>
                    <CardInfo>
                      <span>{item.studentName}</span>
                      <span>Sual {item.questionNumber}</span>
                    </CardInfo>
                    {path ? (
                      <CardImg
                        ref={(el) => setRef(key, el)}
                        src={path}
                        alt={`${item.questionNumber} - ${item.studentName}`}
                        onLoad={() => handleLoad(key)}
                        onError={() => handleError(key)}
                      />
                    ) : (
                      <div style={{ height: 180, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
                        Path yoxdur
                      </div>
                    )}
                  </Card>
                );
              })}
            </Grid>
          </>
        )}

        {rejectedItems.length > 0 && (
          <>
            <SectionTitle>{FOLDER_REJECTED}</SectionTitle>
            <Grid>
              {rejectedItems.map((item) => {
                const key = itemKey(item);
                const path = item.questionData?.path;
                return (
                  <Card key={key}>
                    <CardInfo>
                      <span>{item.studentName}</span>
                      <span>Sual {item.questionNumber}</span>
                    </CardInfo>
                    {path ? (
                      <CardImg
                        ref={(el) => setRef(key, el)}
                        src={path}
                        alt={`${item.questionNumber} - ${item.studentName}`}
                        onLoad={() => handleLoad(key)}
                        onError={() => handleError(key)}
                      />
                    ) : (
                      <div style={{ height: 180, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
                        Path yoxdur
                      </div>
                    )}
                  </Card>
                );
              })}
            </Grid>
          </>
        )}

        {downloadError && <ErrorText>{downloadError}</ErrorText>}

        <Actions>
          <Btn type="button" onClick={handleClose}>
            Geri qayıt
          </Btn>
          <Btn
            $primary
            type="button"
            onClick={handleDownload}
            disabled={!allSettled || downloading || toExport.length === 0}
          >
            {downloading ? 'ZIP hazırlanır...' : 'Indir (ZIP)'}
          </Btn>
        </Actions>
      </ModalBox>
    </ModalOverlay>
  );
};

export default EmptyPageZipModal;
